import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Car, Zap, Fuel, MapPin, Package, Weight, GitCompareArrows, Check, Users, Clock, Gauge, Battery, Timer, BatteryCharging, DoorOpen, Banknote, Ruler, TrendingDown, TrendingUp, HelpCircle, ExternalLink, Newspaper, Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatPrice, formatNumber } from '@/lib/formatters';
import { buildCarImageUrl, buildCarImageSrcSet, CAR_CARD_SIZES } from '@/lib/car-image';
import { FavoriteButton } from '@/components/cars/FavoriteButton';
import { powertrainLabels, drivetrainLabels, transmissionLabels, bodyTypeLabels, getPowertrainBadgeVariant } from '@/types/database';
import { useCompare } from '@/contexts/CompareContext';
import { useSearchDisplaySettings, shouldDisplayValue, type DisplaySetting } from '@/hooks/useDisplaySettings';
import { getRelevantFields, isFieldEmpty } from '@/constants/missing-fields';
import { EQUIPMENT_FIELD_KEYS } from '@/constants/equipment-fields';
import type { Tables } from '@/integrations/supabase/types';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';

interface PriceHistory {
  id: string;
  price_id: string;
  old_price_dkk: number;
  new_price_dkk: number;
  changed_at: string;
}

interface PriceWithHistory extends Tables<'prices_dk'> {
  price_history?: PriceHistory[];
}

export interface CarCardData {
  variant: Tables<'variants'>;
  model: Tables<'models'>;
  brand: Tables<'brands'>;
  lowestPrice?: PriceWithHistory | null;
  specs?: Tables<'powertrain_specs'> | null;
}

interface CarCardProps {
  car: CarCardData;
}

// Icon mapping for dynamic fields
const fieldIcons: Record<string, React.ElementType> = {
  power_hp: Zap,
  range_wltp_km: MapPin,
  l_per_100km: Fuel,
  seats: Users,
  trunk_l: Package,
  tow_kg_braked: Weight,
  acceleration_0_100: Timer,
  battery_kwh: Battery,
  charging_dc_kw: BatteryCharging,
  top_speed_kmh: Gauge,
  doors: DoorOpen,
  owner_tax_yearly_dkk: Banknote,
  green_owner_tax_yearly_dkk: Banknote,
  length_m: Ruler,
  width_m: Ruler,
  height_m: Ruler,
  wheelbase_m: Ruler,
  turning_diameter_m: Ruler,
  warranty_years: Clock,
  warranty_km: Clock,
  battery_warranty_years: Battery,
  battery_warranty_km: Battery,
  euro_ncap: Car,
  heat_pump_standard: Zap,
  fuel_tank_l: Fuel,
};

// Get field value from car data
function getFieldValue(fieldName: string, car: CarCardData): { value: unknown; formatted: string | null } {
  const { variant, model, specs, lowestPrice } = car;
  
  switch (fieldName) {
    case 'price_dkk':
      return { value: lowestPrice?.price_dkk, formatted: lowestPrice?.price_dkk ? formatPrice(lowestPrice.price_dkk) : null };
    case 'powertrain_type':
      return { value: variant.powertrain_type, formatted: powertrainLabels[variant.powertrain_type] };
    case 'range_wltp_km':
      return { value: specs?.range_wltp_km, formatted: specs?.range_wltp_km ? `${formatNumber(specs.range_wltp_km)} km` : null };
    case 'power_hp':
      return { value: specs?.power_hp, formatted: specs?.power_hp ? `${specs.power_hp} hk` : null };
    case 'l_per_100km': return { value: null, formatted: null };
    case 'seats': {
      if (!variant.seats) return { value: null, formatted: null };
      const hasExtra = variant.max_seats && variant.max_seats > variant.seats;
      const optional = hasExtra && variant.extra_seats_optional;
      const seatText = hasExtra ? `${variant.seats}-${variant.max_seats}` : String(variant.seats);
      return { value: variant.seats, formatted: `${seatText}${optional ? '*' : ''} sæder` };
    }
    case 'trunk_l':
      return { value: variant.trunk_l, formatted: variant.trunk_l ? `${formatNumber(variant.trunk_l)} L` : null };
    case 'tow_kg_braked':
      return { value: variant.tow_kg_braked, formatted: variant.tow_kg_braked ? `${formatNumber(variant.tow_kg_braked)} kg` : null };
    case 'acceleration_0_100':
      return { value: specs?.acceleration_0_100, formatted: specs?.acceleration_0_100 ? `${specs.acceleration_0_100} sek` : null };
    case 'battery_kwh':
      return { value: specs?.battery_kwh, formatted: specs?.battery_kwh ? `${specs.battery_kwh} kWh` : null };
    case 'charging_dc_kw':
      return { value: specs?.charging_dc_kw, formatted: specs?.charging_dc_kw ? `${specs.charging_dc_kw} kW` : null };
    case 'top_speed_kmh':
      return { value: specs?.top_speed_kmh, formatted: specs?.top_speed_kmh ? `${specs.top_speed_kmh} km/t` : null };
    case 'drivetrain':
      return { value: variant.drivetrain, formatted: drivetrainLabels[variant.drivetrain] };
    case 'transmission':
      return { value: variant.transmission, formatted: transmissionLabels[variant.transmission] };
    case 'doors':
      return { value: variant.doors, formatted: variant.doors ? `${variant.doors} døre` : null };
    case 'body_type':
      return { value: model.body_type, formatted: bodyTypeLabels[model.body_type] };
    case 'owner_tax_yearly_dkk':
      // Bruges ikke længere — kun CO₂-ejerafgift (green_owner_tax_yearly_dkk) vises.
      return { value: null, formatted: null };
    case 'green_owner_tax_yearly_dkk':
      return { value: variant.green_owner_tax_yearly_dkk, formatted: variant.green_owner_tax_yearly_dkk ? formatPrice(variant.green_owner_tax_yearly_dkk) : null };
    case 'length_m':
      return { value: variant.length_m, formatted: variant.length_m ? `${variant.length_m} m` : null };
    case 'width_m':
      return { value: variant.width_m, formatted: variant.width_m ? `${variant.width_m} m` : null };
    case 'height_m':
      return { value: variant.height_m, formatted: variant.height_m ? `${variant.height_m} m` : null };
    case 'wheelbase_m':
      return { value: variant.wheelbase_m, formatted: variant.wheelbase_m ? `${variant.wheelbase_m} m` : null };
    case 'turning_diameter_m':
      return { value: variant.turning_diameter_m, formatted: variant.turning_diameter_m ? `${variant.turning_diameter_m} m` : null };
    case 'warranty_years':
      return { value: variant.warranty_years, formatted: variant.warranty_years ? `${variant.warranty_years} års garanti` : null };
    case 'warranty_km':
      return { value: variant.warranty_km, formatted: variant.warranty_km ? `${formatNumber(variant.warranty_km)} km garanti` : null };
    case 'battery_warranty_years':
      return { value: variant.battery_warranty_years, formatted: variant.battery_warranty_years ? `${variant.battery_warranty_years} års batterigaranti` : null };
    case 'battery_warranty_km':
      return { value: variant.battery_warranty_km, formatted: variant.battery_warranty_km ? `${formatNumber(variant.battery_warranty_km)} km batterigaranti` : null };
    case 'euro_ncap':
      return { value: variant.euro_ncap, formatted: variant.euro_ncap };
    case 'heat_pump_standard':
      return { value: variant.heat_pump_standard, formatted: variant.heat_pump_standard ? 'Varmepumpe' : null };
    case 'tow_bar_standard':
      return { value: variant.tow_bar_standard, formatted: variant.tow_bar_standard ? 'Trækkrog std.' : null };
    case 'is_commercial':
      return { value: variant.is_commercial, formatted: variant.is_commercial ? 'Erhvervs-elbil' : null };
    case 'fuel_tank_l': return { value: null, formatted: null };
    case 'km_pr_l': return { value: null, formatted: null };
    case 'motorstoerrelse_cc': return { value: null, formatted: null };
    case 'energiklasse': return { value: null, formatted: null };
    case 'udligningsafgift': return { value: null, formatted: null };
    case 'realistisk_vinterraekkevidde_km':
      return { value: specs?.realistisk_vinterraekkevidde_km, formatted: specs?.realistisk_vinterraekkevidde_km ? `${formatNumber(specs.realistisk_vinterraekkevidde_km)} km vinter` : null };
    case 'batteri_brutto_kwh':
      return { value: specs?.batteri_brutto_kwh, formatted: specs?.batteri_brutto_kwh ? `${specs.batteri_brutto_kwh} kWh brutto` : null };
    case 'frunk_liter':
      return { value: variant.frunk_liter, formatted: variant.frunk_liter ? `${variant.frunk_liter} L frunk` : null };
    case 'dc_charge_10_80_min':
      return { value: specs?.dc_charge_10_80_min, formatted: specs?.dc_charge_10_80_min ? `${specs.dc_charge_10_80_min} min (10-80%)` : null };
    case 'charging_ac_kw':
      return { value: specs?.charging_ac_kw, formatted: specs?.charging_ac_kw ? `${specs.charging_ac_kw} kW AC` : null };
    case 'co2_g_km': return { value: null, formatted: null };
    case 'consumption_wh_km':
      return { value: specs?.consumption_wh_km, formatted: specs?.consumption_wh_km ? `${specs.consumption_wh_km} Wh/km` : null };
    case 'torque_nm':
      return { value: specs?.torque_nm, formatted: specs?.torque_nm ? `${formatNumber(specs.torque_nm)} Nm` : null };
    case 'trim_level':
      return { value: variant.trim_level, formatted: variant.trim_level };
    case 'min_wheel_size_inches':
      return { value: variant.min_wheel_size_inches, formatted: variant.min_wheel_size_inches ? `${variant.min_wheel_size_inches}" fælge` : null };
    default:
      return { value: null, formatted: null };
  }
}

// Render a dynamic spec item
function DynamicSpec({ setting, car }: { setting: DisplaySetting; car: CarCardData }) {
  const { value, formatted } = getFieldValue(setting.field_name, car);
  
  // Skip if value is not displayable (null, undefined, N/A, etc.)
  if (!shouldDisplayValue(value) || !formatted) return null;
  
  // Skip powertrain_type and price_dkk as they're handled separately
  if (setting.field_name === 'powertrain_type' || setting.field_name === 'price_dkk') return null;
  
  const Icon = fieldIcons[setting.field_name] || Car;
  
  return (
    <span className="flex items-center gap-1">
      <Icon className="w-3.5 h-3.5" />
      {formatted}
    </span>
  );
}

// Price trend indicator component
function PriceTrendIndicator({ price }: { price: PriceWithHistory }) {
  const history = price.price_history;
  if (!history || history.length === 0) return null;
  
  // Get the most recent price change
  const latestChange = history.sort((a, b) => 
    new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime()
  )[0];
  
  const priceDiff = latestChange.new_price_dkk - latestChange.old_price_dkk;
  
  if (priceDiff === 0) return null;
  
  const isDecrease = priceDiff < 0;
  const formattedDiff = formatNumber(Math.abs(priceDiff));
  
  return (
    <span 
      className={`inline-flex items-center text-xs font-medium ${
        isDecrease ? 'text-green-600' : 'text-destructive'
      }`}
      title={isDecrease 
        ? `Faldet ${formattedDiff} kr` 
        : `Steget ${formattedDiff} kr`
      }
    >
      {isDecrease ? (
        <TrendingDown className="w-4 h-4" />
      ) : (
        <TrendingUp className="w-4 h-4" />
      )}
    </span>
  );
}

export function CarCard({ car }: CarCardProps) {
  const { variant, model, brand, lowestPrice, specs } = car;
  const { addCar, removeCar, isSelected, selectedCars, maxCars } = useCompare();
  const { data: searchSettings = [] } = useSearchDisplaySettings();
  const [imageError, setImageError] = React.useState(false);
  const carImageSource = { variant, model };
  const carImageSrc = buildCarImageUrl(carImageSource, 640);
  const carImageSrcSet = buildCarImageSrcSet(carImageSource);
  
  const isEV = variant.powertrain_type === 'EV';
  const isHybrid = (variant.powertrain_type as string) === 'PHEV' || (variant.powertrain_type as string) === 'Hybrid';
  const isUpcoming = variant.availability_status === 'upcoming';
  const isDiscontinued = variant.availability_status === 'discontinued';
  
  const selected = isSelected(variant.id);
  const canAdd = selectedCars.length < maxCars;

  // Tæl manglende felter for at vise et signal om hvor brugeren kan bidrage
  const missingCount = React.useMemo(() => {
    const all = getRelevantFields(variant.powertrain_type);
    return all.filter(f => {
      if (EQUIPMENT_FIELD_KEYS.has(f.key)) return false;
      if (f.scope === 'variant') return isFieldEmpty((variant as Record<string, unknown>)[f.key]);
      if (f.scope === 'specs') return !specs || isFieldEmpty((specs as Record<string, unknown>)[f.key]);
      if (f.scope === 'price') return !lowestPrice;
      return false;
    }).length;
  }, [variant, specs, lowestPrice]);

  const handleCompareToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (selected) {
      removeCar(variant.id);
    } else if (canAdd) {
      addCar(car);
    }
  };

  return (
    <Link 
      to={`/biler/${brand.slug}/${model.slug}/${variant.slug}`}
      className={`block bg-card rounded-xl shadow-card overflow-hidden card-hover group relative ${selected ? 'ring-2 ring-primary/50' : ''}`}
    >
      {/* Image */}
      <div className="aspect-[16/10] bg-muted relative overflow-hidden">
        {carImageSrc && !imageError ? (
          <img 
            src={carImageSrc} 
            srcSet={carImageSrcSet}
            sizes={CAR_CARD_SIZES}
            alt={`${brand.name} ${model.name}`}
            width={640}
            height={400}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
            <Car className="w-16 h-16 text-muted-foreground/60" />
          </div>
        )}

        {/* Favorit */}
        <FavoriteButton
          variantId={variant.id}
          className="absolute top-2 right-2 z-10 shadow-card"
        />

        {/* Badges */}

        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {isUpcoming && (
            <Badge variant="upcoming" className="gap-1">
              <Clock className="w-3 h-3" />
              Kommer snart
            </Badge>
          )}
          {isDiscontinued && (
            <Badge variant="discontinued" className="gap-1">
              Udgået
            </Badge>
          )}
          <Badge 
            variant={getPowertrainBadgeVariant(variant.powertrain_type)}
          >
            {powertrainLabels[variant.powertrain_type]}
          </Badge>
          {variant.trim_level && (
            <Badge variant="secondary">
              {variant.trim_level}
            </Badge>
          )}
        </div>
        
        {/* Compare CTA (top-right, fremhævet) */}
        <Button
          type="button"
          variant={selected ? 'default' : 'accent'}
          size="sm"
          className="absolute top-3 right-3 z-10 shadow-md min-h-11 gap-1.5"
          onClick={handleCompareToggle}
          disabled={!selected && !canAdd}
          aria-label={selected ? 'Fjern fra sammenligning' : 'Tilføj til sammenligning'}
        >
          {selected ? (
            <>
              <Check className="w-4 h-4" />
              Valgt
            </>
          ) : (
            <>
              <GitCompareArrows className="w-4 h-4" />
              {canAdd ? 'Sammenlign' : `Maks ${maxCars}`}
            </>
          )}
        </Button>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Title */}
        <div>
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">{brand.name}</p>
            {brand.website_url && (
              <a
                href={brand.website_url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary transition-colors"
                title={`Besøg ${brand.name}s officielle side`}
              >
                Officiel side
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          <h3 className="font-display font-semibold text-lg leading-tight">
            {model.name} <span className="text-primary">{variant.name}</span>
          </h3>
        </div>

        {/* Quick Specs - Dynamic based on display_settings */}
        <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
          {searchSettings.map((setting) => (
            <DynamicSpec key={setting.id} setting={setting} car={car} />
          ))}
        </div>

        {/* Drivetrain - only show if not in dynamic settings */}
        {!searchSettings.find(s => s.field_name === 'drivetrain') && (
          <p className="text-xs text-muted-foreground">
            {drivetrainLabels[variant.drivetrain]}
          </p>
        )}

        {/* Expected launch date for upcoming cars */}
        {isUpcoming && variant.expected_launch_date && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Forventet {format(new Date(variant.expected_launch_date), 'MMMM yyyy', { locale: da })}
          </p>
        )}

        {/* Price */}
        <div className="pt-2 border-t border-border">
          {isUpcoming ? (
            variant.expected_price_dkk ? (
              <div>
                <p className="text-xs text-muted-foreground">Forventet pris</p>
                <p className="font-display font-bold text-xl text-primary">
                  {formatPrice(variant.expected_price_dkk)}
                </p>
              </div>
            ) : lowestPrice ? (
              <div>
                <p className="text-xs text-muted-foreground">Forventet pris</p>
                <p className="font-display font-bold text-xl text-primary">
                  {formatPrice(lowestPrice.price_dkk)}
                </p>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">Pris oplyses senere</p>
            )
          ) : lowestPrice ? (
            <div>
              <p className="text-xs text-muted-foreground">Fra</p>
              <div className="flex items-center gap-2">
                <p className="font-display font-bold text-xl text-primary">
                  {formatPrice(lowestPrice.price_dkk)}
                </p>
                <PriceTrendIndicator price={lowestPrice} />
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">Pris ikke tilgængelig</p>
          )}
        </div>

        {/* Antal artikler om denne bil */}
        <ArticleCountPill variantId={variant.id} modelId={model.id} brandSlug={brand.slug} modelSlug={model.slug} variantSlug={variant.slug} />

        {/* Gennemsnitlig brugervurdering */}
        <RatingPill variantId={variant.id} />

        {/* Manglende data — opfordring til at bidrage */}
        {missingCount >= 3 && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-1 border-t border-border-subtle mt-1">
            <HelpCircle className="w-3.5 h-3.5" />
            <span>{missingCount} felter mangler — hjælp os</span>
          </div>
        )}


      </div>
    </Link>
  );
}

function ArticleCountPill({ variantId, modelId }: { variantId: string; modelId: string; brandSlug?: string; modelSlug?: string; variantSlug?: string }) {
  const { data: count } = useQuery({
    queryKey: ['article-count', variantId, modelId],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from('news_post_cars')
        .select('news_post_id, news_posts!inner(is_published)')
        .or(`variant_id.eq.${variantId},model_id.eq.${modelId}`)
        .eq('news_posts.is_published', true);
      const unique = new Set((data || []).map((r: any) => r.news_post_id));
      return unique.size;
    },
  });
  if (!count) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-accent pt-1">
      <Newspaper className="w-3.5 h-3.5" />
      {count} {count === 1 ? 'artikel' : 'artikler'} om denne bil
    </span>
  );
}

function RatingPill({ variantId }: { variantId: string }) {
  const { data } = useQuery({
    queryKey: ['variant-rating', variantId],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.rpc('get_variants_rating_summary', { _ids: [variantId] } as any);
      const row = (data as any[])?.[0];
      if (!row) return null;
      return { avg: Number(row.avg_rating), count: Number(row.rating_count) };
    },
  });
  if (!data || !data.count) return null;
  const formatted = data.avg.toLocaleString('da-DK', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return (
    <span className="inline-flex items-center gap-1 text-xs text-amber-600 pt-1" title={`Gennemsnit af ${data.count} ${data.count === 1 ? 'erfaring' : 'erfaringer'} fra ejere`}>
      <Star className="w-3.5 h-3.5 fill-current" />
      <span className="font-medium">{formatted}</span>
      <span className="text-muted-foreground">({data.count})</span>
    </span>
  );
}

